/**
 * En enda källa för "vad är den här ansökan just nu" och för alla räkningar
 * som översikten, ansökningslistan och ägarvyn visar.
 *
 * Tre vyer räknade "aktiv" på tre olika sätt, och ingen tittade på datumen:
 * en uthyrning som styrelsen godkänt men som börjar den 30 september stod
 * som "Aktiv", och en vars period gått ut stod kvar som aktiv för alltid.
 * Summeringarna på översikten stämde därför inte med listan (Porslinbruket).
 *
 * Fasen kommer från deriveRentalPhase (datum, inte lagrat fält). Här läggs
 * de statusar som inte är uthyrningar till, så att varje ansökan hamnar i
 * exakt en hink.
 */
import { formatDateSv } from './dates';
import { deriveRentalPhase, type RentalLike } from './rentalPhase';

export type ApplicationBucket =
  | 'archived'        // medlem borttagen — döljs som standard
  | 'pending'         // väntar på styrelsens första granskning
  | 'awaitingTenant'  // steg 1 godkänt, hyresgäst saknas
  | 'creditCheck'     // hyresgäst ifylld, kreditkontroll/slutbeslut pågår
  | 'upcoming'        // godkänd uthyrning som inte börjat
  | 'active'
  | 'expiringSoon'    // 30 dagar eller mindre kvar
  | 'terminating'     // uppsägning pågår
  | 'ended'           // perioden har passerat, eller avslutad/utgången status
  | 'rejected'
  | 'other';

export interface ApplicationLike extends RentalLike {
  archivedAt?: unknown;
  extensionRequest?: { status?: string } | null;
}

const CURRENT: ReadonlySet<ApplicationBucket> = new Set<ApplicationBucket>(['upcoming', 'active', 'expiringSoon', 'terminating']);

export function classifyApplication(app: ApplicationLike, today?: string): ApplicationBucket {
  if (app.archivedAt) return 'archived';
  const phase = deriveRentalPhase(app, today);
  if (phase) {
    switch (phase) {
      case 'NOT_STARTED': return 'upcoming';
      case 'ACTIVE': return 'active';
      case 'EXPIRING_SOON': return 'expiringSoon';
      case 'TERMINATING': return 'terminating';
      case 'ENDED': return 'ended';
    }
  }
  switch (String(app.status || '').toUpperCase()) {
    case 'PENDING':
    case 'ADDITIONAL_INFO':
      return 'pending';
    case 'APPROVED':
      return 'awaitingTenant';
    case 'TENANT_INFO_SUBMITTED':
    case 'CREDIT_CHECK_COMPLETE':
    case 'AWAITING_OWNER_DECISION':
      return 'creditCheck';
    case 'COMPLETED':
    case 'EXPIRED':
      return 'ended';
    case 'REJECTED':
    case 'FINAL_REJECTED':
    case 'OWNER_REJECTED':
      return 'rejected';
    default:
      return 'other';
  }
}

/** Uthyrningar som upptar lägenheten i dag eller är godkända men inte börjat. Samma definition överallt. */
export function isCurrentRental(app: ApplicationLike, today?: string): boolean {
  return CURRENT.has(classifyApplication(app, today));
}

export interface ApplicationStats {
  total: number;
  archived: number;
  pending: number;
  awaitingTenant: number;
  creditCheck: number;
  upcoming: number;
  active: number;
  expiringSoon: number;
  terminating: number;
  /** upcoming + active + expiringSoon + terminating */
  current: number;
  ended: number;
  rejected: number;
  other: number;
  extensionRequests: number;
}

export function summarizeApplications(apps: ApplicationLike[], today?: string): ApplicationStats {
  const s: ApplicationStats = {
    total: 0, archived: 0, pending: 0, awaitingTenant: 0, creditCheck: 0, upcoming: 0, active: 0,
    expiringSoon: 0, terminating: 0, current: 0, ended: 0, rejected: 0, other: 0, extensionRequests: 0,
  };
  for (const app of apps) {
    const bucket = classifyApplication(app, today);
    s[bucket] += 1;
    if (bucket === 'archived') continue;
    s.total += 1;
    if (CURRENT.has(bucket)) s.current += 1;
    if (app.extensionRequest?.status === 'pending') s.extensionRequests += 1;
  }
  return s;
}

export type LabelTone = 'green' | 'blue' | 'amber' | 'gray';

/**
 * Etikett för en uthyrning, med startdatumet utskrivet när den inte börjat:
 * "Godkänd, startar 30 september 2026" — inte "Aktiv". null för ansökningar
 * som inte är uthyrningar (då gäller vyns vanliga statusetikett).
 */
export function rentalPhaseLabel(app: ApplicationLike, today?: string): { text: string; tone: LabelTone } | null {
  const phase = deriveRentalPhase(app, today);
  if (!phase) return null;
  switch (phase) {
    case 'NOT_STARTED': {
      const start = formatDateSv(app.contractStartDate || app.fromDate);
      return { text: start ? `Godkänd, startar ${start}` : 'Godkänd, ej startad', tone: 'green' };
    }
    case 'ACTIVE':
      return { text: 'Aktiv uthyrning', tone: 'blue' };
    case 'EXPIRING_SOON': {
      const end = formatDateSv(app.contractEndDate || app.toDate);
      return { text: end ? `Löper ut ${end}` : 'Löper ut snart', tone: 'amber' };
    }
    case 'TERMINATING':
      return { text: 'Uppsägning pågår', tone: 'amber' };
    case 'ENDED':
      return { text: 'Avslutad uthyrning', tone: 'gray' };
  }
}
